import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../components/ui/input-otp';
import { Loader2, Mail, ShieldCheck } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const RESEND_COOLDOWN = 60; // seconds

export default function SignupCodePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const email = params.get('email') || '';
  const planId = params.get('plan') || '';

  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  if (!email || !planId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <h2 className="text-2xl font-bold mb-2">Sessão inválida</h2>
            <p className="text-muted-foreground mb-6">Volte à página inicial.</p>
            <Button onClick={() => navigate('/')}>Voltar</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Extracts a human-readable message from a fetch response, even when the body
  // is malformed or FastAPI returns a `detail` array (422 validation errors).
  const extractErrorMessage = async (res, fallback) => {
    try {
      const data = await res.json();
      if (data && typeof data.detail === 'string') return data.detail;
      if (Array.isArray(data?.detail) && data.detail[0]?.msg) return data.detail[0].msg;
      if (typeof data?.message === 'string') return data.message;
    } catch (_) {
      // body not JSON
    }
    return fallback;
  };

  const submitCode = async (rawCode) => {
    const trimmed = (rawCode || '').replace(/\D/g, '').slice(0, 6);
    if (trimmed.length !== 6) {
      toast.error('Digite os 6 dígitos do código');
      return;
    }
    setSubmitting(true);
    let verifyRes;
    try {
      // 1. Verify code
      verifyRes = await fetch(`${API_URL}/api/signup/verify-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code: trimmed }),
      });
    } catch (error) {
      toast.error('Sem conexão com o servidor. Verifique sua internet e tente novamente.');
      setSubmitting(false);
      return;
    }

    if (!verifyRes.ok) {
      const msg = await extractErrorMessage(verifyRes, 'Não foi possível validar o código.');
      // Friendly mapping by status code
      if (verifyRes.status === 400 && /expirad/i.test(msg)) {
        toast.error('Código expirado. Clique em "Reenviar código" para receber um novo.');
      } else if (verifyRes.status === 400 && /incorret|inválid/i.test(msg)) {
        toast.error(msg); // already includes remaining attempts
      } else if (verifyRes.status === 429) {
        toast.error(msg || 'Muitas tentativas. Solicite um novo código.');
      } else {
        toast.error(msg);
      }
      setCode('');
      setSubmitting(false);
      return;
    }

    let verifyData;
    try {
      verifyData = await verifyRes.json();
    } catch (_) {
      toast.error('Resposta inválida do servidor. Tente novamente.');
      setSubmitting(false);
      return;
    }

    if (!verifyData?.success || !verifyData?.verification_token) {
      toast.error('Não foi possível validar o código. Tente novamente.');
      setSubmitting(false);
      return;
    }

    // 2. Start Stripe Checkout with verification_token
    try {
      const checkoutRes = await fetch(`${API_URL}/api/signup/checkout-direct`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          verification_token: verifyData.verification_token,
          plan_id: planId,
          host_url: window.location.origin,
        }),
      });
      if (!checkoutRes.ok) {
        const msg = await extractErrorMessage(checkoutRes, 'Erro ao iniciar pagamento.');
        toast.error(msg);
        setSubmitting(false);
        return;
      }
      const checkoutData = await checkoutRes.json();
      if (checkoutData?.url) {
        window.location.href = checkoutData.url;
      } else {
        toast.error('Não foi possível abrir o pagamento. Tente novamente.');
        setSubmitting(false);
      }
    } catch (error) {
      toast.error('Sem conexão com o servidor durante o pagamento. Tente novamente.');
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      const res = await fetch(`${API_URL}/api/signup/request-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, plan_id: planId }),
      });
      if (res.ok) {
        toast.success('Novo código enviado! Verifique seu email.');
        setCooldown(RESEND_COOLDOWN);
        setCode('');
      } else {
        const msg = await extractErrorMessage(res, 'Erro ao reenviar código');
        toast.error(msg);
      }
    } catch (error) {
      toast.error('Sem conexão com o servidor. Verifique sua internet e tente novamente.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="max-w-md w-full">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-teal-100">
              <ShieldCheck className="w-5 h-5 text-teal-600" />
            </div>
            <div className="flex-1">
              <CardTitle>Digite o código</CardTitle>
              <CardDescription>
                Enviamos um código de 6 dígitos para <strong>{email}</strong>
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="code" className="sr-only">Código de verificação</Label>
              <div className="flex justify-center py-2">
                <InputOTP
                  maxLength={6}
                  value={code}
                  onChange={(val) => setCode(val.replace(/\D/g, '').slice(0, 6))}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  autoFocus
                  disabled={submitting}
                  data-testid="signup-code-input"
                  onComplete={(val) => {
                    if (val.length === 6 && !submitting) {
                      // small delay so the last digit visually settles
                      setTimeout(() => submitCode(val), 80);
                    }
                  }}
                >
                  <InputOTPGroup className="gap-2">
                    {[0, 1, 2, 3, 4, 5].map((i) => (
                      <InputOTPSlot
                        key={i}
                        index={i}
                        className="h-14 w-12 text-2xl font-bold border-2 rounded-md first:rounded-md last:rounded-md border-l data-[active=true]:border-teal-500 data-[active=true]:ring-2 data-[active=true]:ring-teal-200"
                      />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
              </div>
              <div className="h-6 flex items-center justify-center text-sm">
                {submitting ? (
                  <span className="flex items-center text-teal-600" data-testid="signup-code-verifying">
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Validando código…
                  </span>
                ) : (
                  <span className="text-muted-foreground text-xs">O código expira em 30 minutos.</span>
                )}
              </div>
            </div>
            <div className="flex items-center justify-between text-sm pt-2">
              <Button
                type="button"
                variant="link"
                className="px-0"
                onClick={handleResend}
                disabled={resending || cooldown > 0}
                data-testid="signup-code-resend"
              >
                <Mail className="w-3 h-3 mr-1" />
                {cooldown > 0 ? `Reenviar em ${cooldown}s` : 'Reenviar código'}
              </Button>
              <Button
                type="button"
                variant="link"
                className="px-0 text-muted-foreground"
                onClick={() => navigate(`/signup/email?plan=${encodeURIComponent(planId)}`)}
              >
                Digitei email errado
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
